import { enhancedNavigateTo } from '~/utils/enhancedNavigateTo';
import type { ResolvedPageRedirectPayload } from '~~/server/utils/resolveContentfulPageRedirectDestination';
import { redirectPathsAreEquivalent } from '~/utils/routing/normalizePathForRedirectComparison';
import { isImportMetaServer } from '~/utils/runtime/isImportMetaServer';

/**
 * Applies a Contentful redirect returned by `/api/contentful` during client navigation.
 * SSR redirects are handled by `server/middleware/30.pageRedirect.ts`.
 */
export async function applyContentfulPageRedirect(
  redirect: ResolvedPageRedirectPayload,
  currentPath: string
): Promise<boolean> {
  if (isImportMetaServer()) {
    return false;
  }

  const currentUrl = useRequestURL();
  const destinationUrl = new URL(redirect.destination, currentUrl.origin);

  if (redirectPathsAreEquivalent(destinationUrl.pathname, currentPath)) {
    useLogger().warn(
      `[ContentfulPageRedirect] Prevented endless redirect loop for path: ${currentPath}`
    );
    return false;
  }

  // Pass the full destination URL as `path`. enhancedNavigateTo forces
  // `external: true` for absolute URLs (starts with "http"), so the `query`
  // field would be ignored — use the href directly which already includes params.
  await enhancedNavigateTo(
    { force: true, path: destinationUrl.href },
    { redirectCode: redirect.statusCode }
  );

  return true;
}
